import React from 'react';
import { Row, Col, Button } from 'reactstrap';
import SalesSelectionPanel from './components/SalesSelectionPanel';
import SalesCartPanel from './components/SalesCartPanel';
import SalesReceiptModal from '../../../components/Common/SalesReceiptModal';
import { useNavigate } from 'react-router-dom';
import { useSalesPoint } from './hooks/useSalesPoint';
import OverlayLoader from '../../../components/Common/OverlayLoader';
import EnrollmentGrade from '../../Grade/EnrollmentGrade';
import { formatCurrency } from '../../../utils/format';
import OpenCashierModal from '../../Financial/Cashier/components/OpenCashierModal';

/**
 * Página de Ponto de Venda.
 * Segue o padrão de ser uma "View" limpa, delegando a lógica para o hook useSalesPoint.
 */
const SalesPoint = () => {
    const navigate = useNavigate();

    // Toda a lógica de estado e processamento extraída para o hook
    const {
        memberName,
        activeTab,
        cartItems,
        payments,
        isProcessing,
        isLoadingData,
        data,
        totals,
        toggleTab,
        handleAddItem,
        handleAddPayment,
        handleRemoveItem,
        handleRemovePayment,
        handleFinalizeSale,
        startDate, setStartDate,
        discount, setDiscount,
        saleDate, setSaleDate,
        showReceiptModal,
        saleSuccessData,
        handleCloseReceipt,

        // Matrícula Post-Venda Embed
        pendingEnrollments, setPendingEnrollments,
        showEnrollmentCartId, setShowEnrollmentCartId,

        // Modal de Caixa
        isCashierModalOpen,
        isProcessingShift,
        handleOpenShift,
    } = useSalesPoint();

    const gradeRef = React.useRef(null);

    // Efeito para scroll suave quando o bloco da grid abrir
    React.useEffect(() => {
        if (showEnrollmentCartId && gradeRef.current) {
            gradeRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [showEnrollmentCartId]);

    const handleConfirmEnrollmentDraft = (classes) => {
        setPendingEnrollments(prev => ({ ...prev, [showEnrollmentCartId]: classes }));
        setShowEnrollmentCartId(null);
        window.scrollTo({ top: 0, behavior: 'smooth' }); // Volta pro carrinho
    };

    const handleClearEnrollmentDraft = (cartId) => {
        setPendingEnrollments(prev => {
            const next = { ...prev };
            delete next[cartId];
            return next;
        });
    };

    // 1. Prevenir renderização parcial antes do contexto estar pronto ou dados iniciais carregados
    // Isso elimina o "flicker" de renderizar o esqueleto vazio antes de ter os dados.

    return (
        <div style={{ position: 'relative', minHeight: '400px' }}>
            {/* Cabeçalho da Venda */}
            <div className="d-flex align-items-center justify-content-between px-4 py-3 bg-dark text-white mb-4 rounded shadow-sm">
                <div className="d-flex align-items-center">
                    <Button
                        color="link"
                        className="text-white p-0 me-3"
                        onClick={() => navigate(-1)}
                    >
                        <i className="mdi mdi-arrow-left font-size-22"></i>
                    </Button>
                    <div className="avatar-xs me-3">
                        <span className="avatar-title rounded-circle bg-light text-dark font-size-16">
                            {memberName.charAt(0)}
                        </span>
                    </div>
                    <h5 className="mb-0 text-white font-size-16 text-uppercase">{memberName}</h5>
                </div>

                <div className="d-flex align-items-center gap-4">
                    <div className="d-flex align-items-center">
                        <span className="badge rounded-pill bg-white text-dark font-size-12 me-2">1</span>
                        <span className="font-size-13 text-white">Venda em Aberto</span>
                    </div>
                    <div className="text-end">
                        <span className="d-block font-size-10 text-white-50">Total Final</span>
                        <h5 className="m-0 text-white font-size-16">
                            {formatCurrency(totals?.total || 0)}
                        </h5>
                    </div>
                </div>
            </div>

            <Row className="align-items-start g-3">
                {/* Painel de Seleção (Esquerda) */}
                <Col lg={8}>
                    <SalesSelectionPanel
                        activeTab={activeTab}
                        toggleTab={toggleTab}
                        onAddPayment={handleAddPayment}
                        onAddItem={handleAddItem}
                        memberships={data.memberships}
                        acquirers={data.acquirers}
                        suggestedValue={totals.balance > 0 ? totals.balance : 0}
                        startDate={startDate}
                        setStartDate={setStartDate}
                        discount={discount}
                        setDiscount={setDiscount}
                        saleDate={saleDate}
                        setSaleDate={setSaleDate}
                        cartItems={cartItems}
                        pendingEnrollments={pendingEnrollments}
                        onOpenEnrollment={setShowEnrollmentCartId}
                        onClearEnrollment={handleClearEnrollmentDraft}
                    />
                </Col>

                {/* Resumo da Venda (Direita) */}
                <Col lg={4}>
                    <SalesCartPanel
                        cartItems={cartItems}
                        payments={payments}
                        totals={totals}
                        onRemoveItem={handleRemoveItem}
                        onRemovePayment={handleRemovePayment}
                        onProceed={handleFinalizeSale}
                        pendingEnrollments={pendingEnrollments}
                        onOpenEnrollment={setShowEnrollmentCartId}
                        onClearEnrollment={handleClearEnrollmentDraft}
                    />
                </Col>
            </Row>

            {/* Bloco Condicional de Matrícula Embutida */}
            {showEnrollmentCartId && (
                <div ref={gradeRef} className="mt-5 pt-4 border-top animate__animated animate__fadeInUp">
                    <h5 className="mb-3 text-primary">
                        <i className="mdi mdi-calendar-check text-primary me-2"></i>
                        Selecione as Turmas para Matrícula
                    </h5>
                    <EnrollmentGrade
                        isEmbed={true}
                        embedMode="regular"
                        preSelectedClasses={pendingEnrollments[showEnrollmentCartId] || []}
                        onConfirmEmbed={handleConfirmEnrollmentDraft}
                        onCancelEmbed={() => setShowEnrollmentCartId(null)}
                    />
                </div>
            )}

            {/* Modal de Recibo - Deve vir antes dos loaders para não bugar z-index se loader fechar */}
            <SalesReceiptModal
                isOpen={showReceiptModal}
                toggle={handleCloseReceipt}
                saleData={saleSuccessData}
                memberName={memberName}
            />

            {/* Modal de Caixa Bloqueado (Obrigatório para iniciar venda) */}
            <OpenCashierModal
                isOpen={isCashierModalOpen}
                toggle={() => navigate(-1)} // Se cancelar/fechar o modal, volta pra tela anterior
                onConfirm={handleOpenShift}
                isProcessing={isProcessingShift}
            />

            {/* Loading States - Usando zIndex alto para garantir cobertura */}
            <OverlayLoader show={isLoadingData} label="Carregando dados..." zIndex={1050} />
            <OverlayLoader show={isProcessing} label="Finalizando venda..." zIndex={1051} />
        </div>
    );
};

export default SalesPoint;
